"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Mail,
  User,
  Building2,
  Phone,
  Send,
  MailCheck,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { AuthLayout } from "@/components/layout";
import { Button, Input } from "@/components/ui";
import { authService } from "@/services/authService";
import { handleApiError } from "@/lib/errorHandler";

// forgot-password sayfasiyla ayni desen: bu auth ekranlari metinlerini
// sozlukten degil dogrudan tasiyor.
const MONO_LABEL = "text-[13px] font-medium tracking-[-0.01em] text-muted";

type Fields = {
  name: string;
  email: string;
  venueName: string;
  phone: string;
  message: string;
};

const EMPTY: Fields = {
  name: "",
  email: "",
  venueName: "",
  phone: "",
  message: "",
};

export default function TrialRequestPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>(
    {},
  );
  const [requestError, setRequestError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const set = (key: keyof Fields) => (value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next: Partial<Record<keyof Fields, string>> = {};
    if (fields.name.trim().length < 2) next.name = "Adınızı girin";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email))
      next.email = "Geçerli bir e-posta adresi girin";
    if (fields.venueName.trim().length < 2)
      next.venueName = "Mekan veya organizasyon adını girin";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequestError("");
    if (!validate()) return;

    setIsLoading(true);
    try {
      await authService.requestTrial({
        name: fields.name.trim(),
        email: fields.email.trim(),
        venueName: fields.venueName.trim(),
        phone: fields.phone.trim() || undefined,
        message: fields.message.trim() || undefined,
      });
      setIsSubmitted(true);
    } catch (error) {
      setRequestError(handleApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <AuthLayout brand={{ tagline: "Kapıda isim, içeride kontrol." }}>
        <div className="flex flex-col items-start">
          <span className="mb-[22px] inline-flex h-14 w-14 items-center justify-center rounded-full border border-success-glow-20 bg-success-glow-12 text-success-light">
            <MailCheck
              className="h-[26px] w-[26px]"
              strokeWidth={1.7}
              aria-hidden
            />
          </span>

          <h1 className="font-display text-[30px] font-bold leading-[1.2] tracking-[-0.024em] text-foreground">
            Talebiniz alındı
          </h1>

          <p className="mb-[26px] mt-3 max-w-[340px] text-sm leading-[1.6] text-muted-foreground">
            <span className="break-all font-mono text-[13.5px] font-medium tracking-[-0.01em] text-foreground">
              {fields.email}
            </span>{" "}
            adresinden size döneceğiz. Hesabınızı biz açıp giriş bilgilerinizi
            göndereceğiz.
          </p>

          <div className="flex w-full flex-col gap-3.5">
            <Link
              href="/login"
              className="text-[13px] tracking-[-0.005em] text-muted transition-colors duration-150 hover:text-brand-light"
            >
              Giriş sayfasına dön
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Deneme Talebi"
      description="Venuer şu anda davetle kullanılıyor. Mekanınızı veya organizasyonunuzu anlatın, hesabınızı biz açalım."
      brand={{ tagline: "Kapıda isim, içeride kontrol." }}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {requestError && (
          <div className="flex items-center gap-2.5 rounded-[10px] border border-error-glow-20 bg-error-glow-12 px-3.5 py-3">
            <AlertTriangle
              className="h-4 w-4 shrink-0 text-error-light"
              aria-hidden
            />
            <span className="text-[13px] font-medium tracking-[-0.005em] text-error-light">
              {requestError}
            </span>
          </div>
        )}

        <Input
          id="name"
          label="Ad Soyad"
          labelClassName={MONO_LABEL}
          placeholder="Adınız"
          value={fields.name}
          onChange={(e) => set("name")(e.target.value)}
          error={errors.name}
          leftIcon={User}
          autoComplete="name"
          disabled={isLoading}
        />

        <Input
          id="venueName"
          label="Mekan / Organizasyon"
          labelClassName={MONO_LABEL}
          placeholder="Mekanınızın adı"
          value={fields.venueName}
          onChange={(e) => set("venueName")(e.target.value)}
          error={errors.venueName}
          leftIcon={Building2}
          disabled={isLoading}
        />

        <Input
          id="email"
          label="E-posta"
          labelClassName={MONO_LABEL}
          type="email"
          placeholder="ornek@email.com"
          value={fields.email}
          onChange={(e) => set("email")(e.target.value)}
          error={errors.email}
          leftIcon={Mail}
          autoComplete="email"
          disabled={isLoading}
        />

        <Input
          id="phone"
          label="Telefon (opsiyonel)"
          labelClassName={MONO_LABEL}
          placeholder="+90 5xx xxx xx xx"
          value={fields.phone}
          onChange={(e) => set("phone")(e.target.value)}
          leftIcon={Phone}
          autoComplete="tel"
          disabled={isLoading}
        />

        <div className="space-y-1.5">
          <label htmlFor="message" className={MONO_LABEL}>
            Kısa not (opsiyonel)
          </label>
          <textarea
            id="message"
            rows={3}
            placeholder="Ne sıklıkla etkinlik yapıyorsunuz?"
            value={fields.message}
            onChange={(e) => set("message")(e.target.value)}
            disabled={isLoading}
            className="w-full rounded-[10px] border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted focus:border-brand focus:outline-none disabled:opacity-60"
          />
        </div>

        <Button
          type="submit"
          size="lg"
          className="mt-2 w-full disabled:cursor-default disabled:opacity-100"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Gönderiliyor...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 shrink-0" aria-hidden />
              Talebi Gönder
            </>
          )}
        </Button>

        <div className="flex justify-center pt-1">
          <Link
            href="/login"
            className="inline-flex items-center gap-1.5 text-[13px] tracking-[-0.005em] text-muted transition-colors duration-150 hover:text-brand-light"
          >
            <span className="text-[15px] leading-none">‹</span>
            Giriş sayfasına dön
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
